import { memo, useEffect, useRef } from "react";

import type { Cell } from "@edclub/crosswords-engine";
import { cn } from "@edclub/utils";

import { useCrosswordStore } from "@/store";

interface CellInputProps {
  row: number;
  col: number;
  cellSize: number;
  cell: Cell;
}

export const CellInput = memo(
  ({ row, col, cellSize, cell }: CellInputProps) => {
    const positionedWords = useCrosswordStore.use.positionedWords();
    const setActiveWord = useCrosswordStore.use.setActiveWord();
    const crossword = useCrosswordStore.use.crossword();
    const setActiveCell = useCrosswordStore.use.setActiveCell();
    const updateCellUserAnswer = useCrosswordStore.use.updateCellUserAnswer();

    const inputRef = useRef<HTMLInputElement | null>(null);

    const getCellAt = (targetRow: number, targetCol: number) => {
      if (
        crossword &&
        crossword.grid &&
        crossword.grid[targetRow] &&
        crossword.grid[targetRow][targetCol]
      ) {
        return crossword.grid[targetRow][targetCol];
      }

      return null;
    };

    const gridHeight = crossword?.grid.length || 0;
    const gridWidth = crossword?.grid[0]?.length || 0;

    useEffect(() => {
      inputRef.current?.focus();
    }, []);

    const moveActiveCell = (newRow: number, newCol: number) => {
      if (
        newRow < 0 ||
        newRow >= gridHeight ||
        newCol < 0 ||
        newCol >= gridWidth
      ) {
        return;
      }

      const targetCell = getCellAt(newRow, newCol);

      if (targetCell && targetCell.letter !== "") {
        setActiveCell(targetCell);
        setActiveWord(targetCell.words[0]);
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (
        e.key === "ArrowLeft" ||
        e.key === "ArrowRight" ||
        e.key === "ArrowUp" ||
        e.key === "ArrowDown" ||
        e.key === "Backspace"
      ) {
        e.preventDefault();
      }

      if (e.key === "ArrowLeft") {
        moveActiveCell(row, col - 1);
      } else if (e.key === "ArrowRight") {
        moveActiveCell(row, col + 1);
      } else if (e.key === "ArrowUp") {
        moveActiveCell(row - 1, col);
      } else if (e.key === "ArrowDown") {
        moveActiveCell(row + 1, col);
      } else if (e.key === "Backspace") {
        const targetCell = getCellAt(row, col);

        if (!cell.revealed) {
          updateCellUserAnswer(row, col, "");
        }

        const direction = positionedWords.find(
          (w) => w.id === targetCell!.words[0],
        )!.direction;

        if (direction === "across") {
          if (col > 0) {
            moveActiveCell(row, col - 1);
          } else if (row > 0) {
            moveActiveCell(row - 1, gridWidth - 1);
          }
        } else if (direction === "down") {
          if (row > 0) {
            moveActiveCell(row - 1, col);
          }
        }
        // When user types in a character
      } else if (e.key.length === 1 && /^[-a-zA-Z_]$/.test(e.key)) {
        const newLetter = e.key.toUpperCase();

        if (cell.revealed) {
          updateCellUserAnswer(row, col, cell.letter.toUpperCase());
        } else {
          updateCellUserAnswer(row, col, newLetter);
        }
      }
    };

    return (
      <input
        ref={inputRef}
        className={cn(
          "rounded-sm border-none text-center font-semibold text-[#42576e] outline-none",
          !cell.revealed && "focus-within:bg-[#ffe2a7]",
        )}
        style={{
          width: `${cellSize}px`,
          height: `${cellSize}px`,
          lineHeight: `${cellSize}px`,
        }}
        maxLength={1}
        type="text"
        value={cell.userAnswer || ""}
        onChange={() => {}}
        onKeyDown={handleKeyDown}
        aria-label={t(
          "Crossword {{revealed}} cell at row {{row}}, column {{col}}, current value: {{value}}",
          {
            row: row + 1,
            col: col + 1,
            value: cell.revealed
              ? cell.letter.toUpperCase()
              : cell.userAnswer || "not provided",
            revealed: cell.revealed ? "revealed" : "",
          },
        )}
        title={t(
          "Crossword {{revealed}} cell at row {{row}}, column {{col}}, current value: {{value}}",
          {
            row: row + 1,
            col: col + 1,
            value: cell.revealed
              ? cell.letter.toUpperCase()
              : cell.userAnswer || "not provided",
            revealed: cell.revealed ? "revealed" : "",
          },
        )}
      />
    );
  },
);
